import { startTransition, useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
  MessageDirectionName,
  MessageOriginName,
  MessageStatusName,
  MessageTypeName,
  OrganizationMemberRoleName,
  PlatformTypeName,
  ReportMessageListResponse,
  ReportMessageOptionsResponse,
  ReportMessagePageSize,
  ReportMessageSortName,
} from "@multi-chat/shared";
import {
  getReportMessagesOptions,
  listMessageLogMessages,
} from "../../api/reports-api.js";
import {
  clearMessageLogFilters,
  clearOutboundOnlyFilters,
  hasActiveMessageLogFilters,
  messageLogHref,
  parseMessageLogQuery,
  type MessageLogQuery,
} from "./message-log-query.js";

const SEARCH_DEBOUNCE_MS = 280;

export function useMessageLogReport(input: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): {
  query: MessageLogQuery;
  list: ReportMessageListResponse | null;
  options: ReportMessageOptionsResponse | null;
  loading: boolean;
  error: unknown;
  filtersActive: boolean;
  canAccess: boolean;
  dateFrom: string;
  dateTo: string;
  setSearch: (search: string) => void;
  setDateRange: (from: string, to: string) => void;
  setDirection: (direction: MessageDirectionName | undefined) => void;
  setPlatform: (platform: PlatformTypeName | undefined) => void;
  setEndpointId: (endpointId: number | undefined) => void;
  setMessageType: (messageType: MessageTypeName | undefined) => void;
  setStatus: (status: MessageStatusName | undefined) => void;
  setOrigin: (origin: MessageOriginName | undefined) => void;
  setCampaignId: (campaignId: number | undefined) => void;
  setTemplate: (template: string | undefined) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: ReportMessagePageSize) => void;
  setSort: (sort: ReportMessageSortName) => void;
  resetFilters: () => void;
  refresh: () => void;
} {
  const canAccess = input.role === "OWNER" || input.role === "ADMIN";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const query = useMemo(
    () => parseMessageLogQuery(searchParamsKey),
    [searchParamsKey],
  );

  const [searchDraft, setSearchDraft] = useState(query.search);
  const [debouncedSearch, setDebouncedSearch] = useState(query.search);
  const [list, setList] = useState<ReportMessageListResponse | null>(null);
  const [options, setOptions] = useState<ReportMessageOptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    setSearchDraft(query.search);
    setDebouncedSearch(query.search);
  }, [query.search]);

  const syncSearchToUrl = useEffectEvent((nextSearch: string) => {
    if (nextSearch === query.search) return;
    navigate(messageLogHref({ ...query, search: nextSearch, page: 1 }), { replace: true });
  });

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchDraft.trim();
      setDebouncedSearch(next);
      syncSearchToUrl(next);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchDraft]);

  const applyQuery = useCallback(
    (next: MessageLogQuery) => {
      navigate(messageLogHref(next), { replace: true });
    },
    [navigate],
  );

  const patchQuery = useCallback(
    (patch: Partial<MessageLogQuery>, resetPage = true) => {
      applyQuery({
        ...query,
        ...patch,
        page: resetPage ? 1 : (patch.page ?? query.page),
      });
    },
    [applyQuery, query],
  );

  const load = useEffectEvent(async (signal: AbortSignal) => {
    if (!canAccess) return;
    setLoading(true);
    setError(null);
    try {
      const listRes = await listMessageLogMessages(
        input.organizationId,
        {
          ...(query.from && query.to ? { fromDate: query.from, toDate: query.to } : {}),
          direction: query.direction,
          platform: query.platform,
          endpointId: query.endpointId,
          messageType: query.messageType,
          status: query.status,
          origin: query.origin,
          campaignId: query.campaignId,
          template: query.template,
          search: debouncedSearch.trim() || undefined,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
        },
        signal,
      );
      if (signal.aborted) return;
      startTransition(() => {
        setList(listRes);
      });
    } catch (err) {
      if (signal.aborted) return;
      setError(err);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  });

  const loadOptions = useEffectEvent(async (signal: AbortSignal) => {
    if (!canAccess) return;
    try {
      const optionsRes = await getReportMessagesOptions(input.organizationId, {}, signal);
      if (signal.aborted) return;
      startTransition(() => {
        setOptions(optionsRes);
      });
    } catch {
      // Options failure is non-fatal; list error path handles primary UX.
    }
  });

  useEffect(() => {
    if (!canAccess) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [
    canAccess,
    input.organizationId,
    query.from,
    query.to,
    query.direction,
    query.platform,
    query.endpointId,
    query.messageType,
    query.status,
    query.origin,
    query.campaignId,
    query.template,
    query.page,
    query.pageSize,
    query.sort,
    debouncedSearch,
    refreshToken,
  ]);

  useEffect(() => {
    if (!canAccess) return;
    const controller = new AbortController();
    void loadOptions(controller.signal);
    return () => controller.abort();
  }, [canAccess, input.organizationId, refreshToken]);

  return {
    query: { ...query, search: searchDraft },
    list,
    options,
    loading,
    error,
    filtersActive: hasActiveMessageLogFilters({ ...query, search: searchDraft }),
    canAccess,
    dateFrom: query.from ?? list?.fromDate ?? "",
    dateTo: query.to ?? list?.toDate ?? "",
    setSearch: setSearchDraft,
    setDateRange: (from, to) => {
      if (!from || !to) {
        const next = { ...query };
        delete next.from;
        delete next.to;
        applyQuery({ ...next, page: 1 });
        return;
      }
      patchQuery({ from, to });
    },
    setDirection: (direction) => {
      if (direction === "INBOUND") {
        applyQuery({
          ...clearOutboundOnlyFilters(query),
          direction: "INBOUND",
          page: 1,
        });
        return;
      }
      if (direction == null) {
        const next = { ...query, page: 1 };
        delete next.direction;
        applyQuery(next);
        return;
      }
      patchQuery({ direction });
    },
    setPlatform: (platform) => patchQuery({ platform }),
    setEndpointId: (endpointId) => patchQuery({ endpointId }),
    setMessageType: (messageType) => patchQuery({ messageType }),
    setStatus: (status) => patchQuery({ status }),
    setOrigin: (origin) => patchQuery({ origin }),
    setCampaignId: (campaignId) => patchQuery({ campaignId }),
    setTemplate: (template) => patchQuery({ template }),
    setPage: (page) => patchQuery({ page }, false),
    setPageSize: (pageSize) => patchQuery({ pageSize, page: 1 }),
    setSort: (sort) => patchQuery({ sort }),
    resetFilters: () => applyQuery(clearMessageLogFilters(query)),
    refresh: () => setRefreshToken((n) => n + 1),
  };
}
